import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Activity } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";
import { GoogleRatingBadge } from "@/components/GoogleRatingBadge";

const LINES = ["Get More Customers.", "Keep Every Lead.", "Know What's Working."];

export const Hero = () => {
  const { scrollY } = useScroll();
  const yGlow = useTransform(scrollY, [0, 700], [0, 200]);
  const yGrid = useTransform(scrollY, [0, 700], [0, 100]);
  const fade = useTransform(scrollY, [0, 500], [1, 0]);

  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden pt-16" data-testid="hero-section">
      <motion.div style={{ y: yGrid }} className="hero-grid-bg absolute inset-0" />
      <motion.div
        style={{ y: yGlow }}
        className="absolute -top-40 left-1/2 -translate-x-1/2 h-[560px] w-[860px] rounded-full bg-[#12264d] opacity-60 blur-[140px]"
      />
      <motion.div
        style={{ y: yGlow }}
        className="absolute bottom-0 right-[-10%] h-[380px] w-[380px] rounded-full bg-[#0d1c3a] opacity-70 blur-[120px]"
      />

      <motion.div style={{ opacity: fade }} className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 w-full">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="font-mono-label text-xs uppercase tracking-[0.3em] text-slate-400 mb-10"
        >
          Chapter 01 // OG Solutions — For Local Service Businesses
        </motion.p>

        <h1
          data-testid="hero-headline-text"
          className="font-display text-4xl sm:text-6xl lg:text-8xl font-extrabold tracking-tight leading-[1.02] text-white"
        >
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 1 ? "text-outline" : ""}`}
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 max-w-xl text-base sm:text-lg text-slate-400 leading-relaxed"
        >
          We build fast, lead-focused websites for service businesses — and every
          month we check your site, Google ranking, and lead response speed, so
          you keep winning jobs instead of guessing what's working.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center gap-6"
        >
          <button
            data-testid="hero-book-call-cta"
            onClick={() => scrollToSection("#contact-form")}
            className="group inline-flex items-center gap-3 rounded-full bg-white px-8 py-4 text-base font-semibold text-[#060D1A] transition-colors duration-300 hover:bg-slate-300"
          >
            Book My Free Call
            <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
          </button>
          <span className="font-mono-label text-xs uppercase tracking-[0.25em] text-slate-500">
            Free audit on the call // No contracts
          </span>
        </motion.div>

        <GoogleRatingBadge />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 40, rotate: -3 }}
        animate={{ opacity: 1, y: 0, rotate: -3 }}
        transition={{ duration: 1, delay: 1.3, ease: [0.22, 1, 0.36, 1] }}
        className="absolute bottom-24 right-6 lg:right-16 hidden md:block"
      >
        <motion.div
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          className="rounded-2xl border border-white/15 bg-white/[0.04] backdrop-blur-xl px-6 py-5"
          data-testid="hero-floating-badge"
        >
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-[#060D1A]">
              <Activity className="h-4 w-4" />
            </span>
            <div>
              <p className="font-mono-label text-[10px] uppercase tracking-[0.25em] text-slate-400">Monthly Report</p>
              <p className="text-sm font-semibold text-white">5 pillars tracked</p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};
